import React, { useState } from "react";
import { StyleSheet, Platform, LayoutChangeEvent } from "react-native";
import { useThemeSettings } from "../ThemeProvider";
import { usePlatform } from "../providers/PlatformProvider";
import { LinearGradient as LinearGradientWeb } from 'expo-linear-gradient';
import getGlobalStyles from "../globalStyles";

export enum GRADIENT_TYPE {
  BASE,
  BASE_WEB,
  MODAL,
  ARTICLE_CARD,
  APP_CARD,
  UNDERLINE,
  CARD1,
  CARD2,
  CARD3
}

const gradientTopBottomDirection = {
  start: { x: 0.5, y: 0 },  // top
  end: { x: 0.5, y: 1 },   // bottom
}
const CustomLinearGradient = ({ children = null, gradientType, angle = 135, style }: { children?: React.ReactNode, gradientType: GRADIENT_TYPE, style?: any, angle?: number }) => {
  const { isDark, theme } = useThemeSettings();
  const gStyles = getGlobalStyles();
  const { showWebVersion } = usePlatform();
  const [size, setSize] = useState({ width: 0, height: 0 });
  const onLayout = (e: LayoutChangeEvent) => {
    const { width, height } = e.nativeEvent.layout;
    setSize({ width, height });
  };
  function cssAngleToRN(angleDeg: number) {
    if (size?.width && size?.height) {
      // CSS: 0° = up, clockwise
      const rad = (angleDeg * Math.PI) / 180;

      // Screen-space direction
      const dx = Math.sin(rad);
      const dy = -Math.cos(rad);

      // Compensate for aspect ratio
      const vx = dx / size?.width;
      const vy = dy / size?.height;

      // Normalize
      const len = Math.sqrt(vx * vx + vy * vy);
      const nx = vx / len;
      const ny = vy / len;

      return {
        start: {
          x: 0.5 - nx / 2,
          y: 0.5 - ny / 2,
        },
        end: {
          x: 0.5 + nx / 2,
          y: 0.5 + ny / 2,
        },
      };
    }
  }
  const getGradientProps = () => {
    switch (gradientType) {
      case GRADIENT_TYPE.BASE:
        return {
          colors:  [theme?.colors?.appBackground2, theme?.colors?.appBackground3],
          locations: [0, 1],
          ...cssAngleToRN(0),
          style: [StyleSheet.absoluteFill, style]
        }
        //redunctant to support AI_assistant BG
        case GRADIENT_TYPE.BASE_WEB:
          return {
            colors: [theme?.colors?.appBackground2Web, theme?.colors?.appBackground3Web],
            locations: [0.1745, 1],
            ...cssAngleToRN( angle),
            style: [StyleSheet.absoluteFill, style]
          }
      case GRADIENT_TYPE.CARD1:
        return {
          colors: [theme?.colors?.shareCard1Gradient1, theme?.colors?.shareCard1Gradient2],
          ...cssAngleToRN(angle),
          style: [gStyles.gradientBorderShadow, style]
        }

      case GRADIENT_TYPE.CARD2:
        return {
          colors: [theme?.colors?.shareCard2Gradient1, theme?.colors?.shareCard2Gradient2],
          ...cssAngleToRN(angle),
          style: [gStyles.gradientBorderShadow, style]
        }
      case GRADIENT_TYPE.CARD3:
        return {
          colors: [theme?.colors?.shareCard3Gradient1, theme?.colors?.shareCard3Gradient2],
          ...cssAngleToRN(angle),
          style: [gStyles.gradientBorderShadow, style]
        }

      case GRADIENT_TYPE.MODAL:
        return {
          colors: [theme?.colors?.appBackground2, theme?.colors?.appBackground1],
          ...cssAngleToRN(angle),
          style: [gStyles.gradientBorderShadow, style]
        }
      case GRADIENT_TYPE.APP_CARD:
        return {
          colors: showWebVersion ? [theme?.colors?.appCard1Web, theme?.colors?.appCard2Web] : [theme?.colors?.appCard1, theme?.colors?.appCard2],
          ...cssAngleToRN(showWebVersion ? angle: 0),
          style: [gStyles.gradientBorderShadow, style]
        }
      case GRADIENT_TYPE.ARTICLE_CARD:
        return {
          colors: [
            'rgba(58,34,8,0)',     // 0%
            'rgba(58,34,8,0.85)',  // 47%
            'rgba(58,34,8,1)',     // 100%
          ],
          locations: [0, 0.47, 1],
          ...gradientTopBottomDirection,
          style: [StyleSheet.absoluteFill, style]
        }
      case GRADIENT_TYPE.UNDERLINE:
        return {
          colors: [
            theme?.colors?.primary,
            theme?.colors?.primaryLight,
            theme?.colors?.primaryLight2,
          ],
          ...cssAngleToRN(135),
          style: styles.underline
        }
    }
  }
  if (Platform.OS !== 'web') {
    const LinearGradient = require('react-native-linear-gradient').default;
    return <LinearGradient onLayout={onLayout} {...getGradientProps()}>{children}</LinearGradient>
  } else {
    return <LinearGradientWeb onLayout={onLayout} {...getGradientProps()}>{children}</LinearGradientWeb>;
  };
}

export default CustomLinearGradient;

const styles = StyleSheet.create({
  underline: {
    marginTop: 6,
    height: 3,
    borderRadius: 2,
  },
});
